/**
 * useKeyboardShortcuts — global keyboard shortcut handler.
 *
 * Registers a single `keydown` listener on `document` and dispatches
 * Redux actions or callback functions for matching key combinations.
 *
 * ### Modifier handling
 * Supports `Ctrl`, `Shift`, `Alt` modifiers (or `Meta` on macOS).
 * The `key` comparison is case-insensitive.
 *
 * ### Built-in shortcuts (Chart page)
 * | Shortcut            | Action                               |
 * |---------------------|--------------------------------------|
 * | `Ctrl + Shift + N`  | Add new chart panel                  |
 * | `Ctrl + M`          | Toggle Market Watch                  |
 * | `Ctrl + Shift + T`  | Cycle theme (light/dark)             |
 * | `Ctrl + +`          | Zoom in on active chart              |
 * | `Ctrl + -`          | Zoom out on active chart             |
 * | `Ctrl + 0`          | Scroll to latest bar                 |
 * | `Ctrl + Shift + S`  | Screenshot active chart              |
 * | `Escape`            | Reset drawing tool to cursor         |
 * | `Delete`            | Undo last drawing                    |
 * | `1`–`9`             | Quick timeframe (1=1m, 5=5m, etc.)   |
 *
 * The hook is idempotent — mount it once in `App.tsx` or `WithNav.tsx`.
 */
import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addPanel, toggleMarketWatch, toggleToolsSidebar } from "@store/chart-slice";
import { themeReducer } from "@store/theme-slice";
import { getColorTheme } from "@service/local-storage-handler";

export interface ShortcutEntry {
  /** Lowercase key value (e.g. "n", "m", "+", "escape") */
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Description shown in shortcut help (optional) */
  description?: string;
  /** Action callback — receives the KeyboardEvent. Return false to cancel. */
  action: (e: KeyboardEvent) => void | false;
}

/**
 * Global shortcut registry.
 *
 * Accepts an optional array of extra shortcuts that the caller wants
 * to register on top of the built-in set.
 */
export function useKeyboardShortcuts(
  extraShortcuts?: ShortcutEntry[],
  options?: {
    /** Ref to the active chart panel's imperative handle */
    activePanelRef?: React.RefObject<any>;
    /** Callback to reset drawing tool to cursor */
    onResetDrawingTool?: () => void;
    /** Whether shortcuts are enabled (default true) */
    enabled?: boolean;
  }
) {
  const dispatch = useDispatch();
  const enabled = options?.enabled ?? true;

  // Stable refs to avoid re-registering on every render
  const activePanelRefStable = useRef(options?.activePanelRef);
  activePanelRefStable.current = options?.activePanelRef;

  const onResetDrawingToolRef = useRef(options?.onResetDrawingTool);
  onResetDrawingToolRef.current = options?.onResetDrawingTool;

  const extraRef = useRef(extraShortcuts);
  extraRef.current = extraShortcuts;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Skip when the user is typing in an input or textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      const alt = e.altKey;

      // ── Built-in shortcuts ──────────────────────────────────

      // Ctrl+Shift+N → Add panel
      if (ctrl && shift && key === "n") {
        e.preventDefault();
        dispatch(addPanel());
        return;
      }

      // Ctrl+M → Toggle Market Watch
      if (ctrl && !shift && key === "m") {
        e.preventDefault();
        dispatch(toggleMarketWatch());
        return;
      }

      // Ctrl+T → Toggle Tools Sidebar
      if (ctrl && !shift && key === "t") {
        e.preventDefault();
        dispatch(toggleToolsSidebar());
        return;
      }

      // Ctrl+Shift+T → Toggle theme
      if (ctrl && shift && key === "t") {
        e.preventDefault();
        const current = getColorTheme() || "dark";
        const next = current === "dark" ? "light" : "dark";
        dispatch(themeReducer({ theme: next }));
        return;
      }

      // Ctrl + / Ctrl - → Zoom in/out
      if (ctrl && !shift && (key === "=" || key === "+")) {
        e.preventDefault();
        activePanelRefStable.current?.current?.current?.zoomIn?.();
        return;
      }
      if (ctrl && !shift && key === "-") {
        e.preventDefault();
        activePanelRefStable.current?.current?.current?.zoomOut?.();
        return;
      }

      // Ctrl+0 → Scroll to latest
      if (ctrl && !shift && key === "0") {
        e.preventDefault();
        activePanelRefStable.current?.current?.current?.scrollToLatest?.();
        return;
      }

      // Ctrl+Shift+S → Screenshot
      if (ctrl && shift && key === "s") {
        e.preventDefault();
        activePanelRefStable.current?.current?.current?.takeScreenshot?.();
        return;
      }

      // Escape → Reset drawing tool
      if (key === "escape" && !ctrl && !shift && !alt) {
        onResetDrawingToolRef.current?.();
        return;
      }

      // Delete → Undo last drawing
      if (key === "delete" && !ctrl && !shift && !alt) {
        activePanelRefStable.current?.current?.current?.undoDrawing?.();
        return;
      }

      // ── Extra (caller-provided) shortcuts ───────────────────
      const extras = extraRef.current;
      if (extras) {
        for (const s of extras) {
          if (
            s.key === key &&
            !!s.ctrl === ctrl &&
            !!s.shift === shift &&
            !!s.alt === alt
          ) {
            e.preventDefault();
            s.action(e);
            return;
          }
        }
      }
    },
    [dispatch, enabled]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

export default useKeyboardShortcuts;
